<template>
  <div class="stats-page">
    <h1 class="page-title">Statistics</h1>
    <div class="stats-content">
      <div class="stats-boxes">
        <!-- Streak -->
        <div class="stat-box">
          <p class="stat-box-title">Streak</p>
          <div class="stat-box-info">
            <p class="stat-box-stat" v-fit-text>🔥 {{ stats.currentStreak }}</p>
            <p class="stat-box-caption">{{ stats.currentStreak === 1 ? 'day' : 'days' }}</p>
          </div>
        </div>
        <!-- Max Streak -->
        <div class="stat-box">
          <p class="stat-box-title">Max Streak</p>
          <div class="stat-box-info">
            <p class="stat-box-stat" v-fit-text>🔥 {{ stats.maxStreak }}</p>
            <p class="stat-box-caption">{{ stats.maxStreak === 1 ? 'day' : 'days' }}</p>
          </div>
        </div>
        <!-- Total Played -->
        <div class="stat-box">
          <p class="stat-box-title">Total Played</p>
          <div class="stat-box-info">
            <p class="stat-box-stat" v-fit-text>🎲 {{ stats.totalPlayed }}</p>
            <p class="stat-box-caption">{{ stats.totalPlayed === 1 ? 'game' : 'games' }}</p>
          </div>
        </div>
        <!-- Average Score -->
        <div class="stat-box">
          <p class="stat-box-title">Average Score</p>
          <div class="stat-box-info">
            <p class="stat-box-stat" v-fit-text>{{ stats.averageScore }}</p>
          </div>
        </div>
        <!-- Best Score -->
        <div class="stat-box">
          <p class="stat-box-title">Best Score</p>
          <div class="stat-box-info">
            <p class="stat-box-stat" v-fit-text>🚀 {{ stats.bestScore }}</p>
          </div>
        </div>
        <!-- Win % -->
        <div class="stat-box">
          <p class="stat-box-title">Win %</p>
          <div class="stat-box-info">
            <p class="stat-box-stat" v-fit-text>{{ stats.winPercentage }}%</p>
          </div>
        </div>
        <!-- Total Legendaries -->
        <div class="stat-box legendaries">
          <p class="stat-box-title">Total Legendaries</p>
          <div class="stat-box-info">
            <p class="stat-box-stat" v-fit-text>{{ stats.legendaries }}</p>
          </div>
        </div>
        <!-- Total Epics -->
        <div class="stat-box epics">
          <p class="stat-box-title">Total Epics</p>
          <div class="stat-box-info">
            <p class="stat-box-stat" v-fit-text>{{ stats.epics }}</p>
          </div>
        </div>
        <!-- Total Rares -->
        <div class="stat-box rares">
          <p class="stat-box-title">Total Rares</p>
          <div class="stat-box-info">
            <p class="stat-box-stat" v-fit-text>{{ stats.rares }}</p>
          </div>
        </div>
      </div>

      <score-distribution-graph
        v-if="loaded"
        :score-distribution="stats.scoreDistribution"
        :infinity-score-distribution="stats.infinityScoreDistribution"
      ></score-distribution-graph>
    </div>
  </div>
</template>

<script>
  import { getStatsData } from '@/utils/stats';
  import fitText from '@/directives/fitText';
  import ScoreDistributionGraph from './ScoreDistributionGraph.vue';

  export default {
    components: {
      ScoreDistributionGraph,
    },
    directives: {
      fitText,
    },
    data() {
      return {
        loaded: false,
        stats: {
          currentStreak: 0,
          maxStreak: 0,
          averageScore: 0,
          bestScore: 0,
          totalPlayed: 0,
          winPercentage: 0,
          legendaries: 0,
          epics: 0,
          rares: 0,
          scoreDistribution: {
            '0-99': 0,
            '100-199': 0,
            '200-299': 0,
            '300-399': 0,
            '400-499': 0,
            '500-599': 0,
            '600-699': 0,
            '700-799': 0,
            '800-900': 0,
          },
          infinityScoreDistribution: {
            '0-99': 0,
            '100-199': 0,
            '200-299': 0,
            '300-399': 0,
            '400-499': 0,
            '500-599': 0,
            '600-699': 0,
            '700-799': 0,
            '800-900': 0,
          },
        },
      };
    },
    mounted() {
      this.stats = getStatsData();
      this.loaded = true;
    },
  };
</script>

<style scoped>
  .stats-page {
    margin: 0 auto;
    margin-bottom: 36px;
    padding: 0 20px;
    color: white;
    box-sizing: border-box;
  }

  h1 {
    margin: 0;
  }

  .page-title {
    text-align: center;
    margin-bottom: 24px;
    font-weight: 700;
    font-size: 24px;
  }

  .stats-content {
    display: flex;
    flex-direction: column;
    gap: 24px;
    align-items: center;
    justify-content: center;
  }

  .stats-boxes {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 140px));
    gap: 24px;
    justify-content: center;
  }

  .stat-box {
    width: 100%;
    max-width: 140px;
    background-color: rgb(76, 76, 103);
    border-radius: 16px;
    padding: 6px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    justify-content: space-between;
    box-sizing: border-box;
  }

  .stat-box.legendaries {
    background-color: rgb(225, 53, 53);
  }

  .stat-box.epics {
    background-color: rgb(221, 112, 197);
  }

  .stat-box.rares {
    background-color: rgb(150, 112, 221);
  }

  .stat-box-title {
    color: white;
    font-weight: 700;
    font-size: 12px;
    margin: 0;
    text-align: center;
    line-height: 1.3;
  }

  .stat-box-info {
    width: 100%;
    background-color: rgba(53, 53, 72, 1);
    border-radius: 12px;
    padding: 8px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    box-sizing: border-box;
    text-align: center;
    gap: 6px;
  }

  .stat-box-stat {
    color: white;
    font-weight: 700;
    font-size: 32px;
    margin: 0;
    text-align: center;
    line-height: 1;
    white-space: nowrap;
  }

  .stat-box-caption {
    color: white;
    font-weight: 200;
    font-size: 12px;
    margin: 0;
    text-align: center;
    line-height: 1;
  }

  @media screen and (max-width: 768px) {
    .stats-page {
      padding: 0 8px;
    }

    .stats-boxes {
      gap: 12px;
    }

    .stat-box-stat {
      font-size: 22px;
    }
  }
</style>
